import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useTrash } from '../hooks/useTrash';
import Wordmark from '../components/Wordmark';

/**
 * TrashPage — accessible at /trash from the account popover. Shows
 * the user's soft-deleted boards and lists with restore + delete-now
 * actions per item. Items auto-expire (hard-delete) after 30 days
 * via the daily `expireTrashItems` Cloud Function.
 *
 * Owner-trashes-and-restores their own content directly. For shared
 * lists where a guest editor did the deletion, the trash entry lives
 * under the OWNER's uid (per `trashList` Cloud Function), so the
 * owner sees + controls the restore — guest only sees the list
 * disappear.
 */
export default function TrashPage({ user }) {
  const navigate = useNavigate();
  const { items, loading } = useTrash(user?.uid);
  const [actionPending, setActionPending] = useState(null); // trashId being acted on
  const [errorMsg, setErrorMsg] = useState(null);
  const [toast, setToast] = useState(null);

  const flashToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(t => t === msg ? null : t), 3500);
  };

  const onRestore = async (item) => {
    setActionPending(item.id);
    setErrorMsg(null);
    try {
      const fn = httpsCallable(functions, 'restoreFromTrash');
      const res = await fn({ trashId: item.id });
      const data = res?.data || {};
      if (data.kind === 'board') {
        flashToast(`restored board → opening`);
        setTimeout(() => navigate(`/board/${data.boardId}`), 700);
      } else if (data.kind === 'list') {
        if (data.movedToRecovered) {
          flashToast('restored to "Recovered" (original board was also trashed)');
          setTimeout(() => navigate(`/board/${data.boardId}`), 1000);
        } else {
          flashToast('restored to original board');
          setTimeout(() => navigate(`/board/${data.boardId}`), 700);
        }
      } else {
        flashToast('restored');
      }
    } catch (err) {
      console.warn('[TrashPage] restore failed', err);
      setErrorMsg(err?.message || 'restore failed');
    } finally {
      setActionPending(null);
    }
  };

  const onHardDelete = async (item) => {
    if (!window.confirm(
      `permanently delete this ${item.kind}?\n\n`
      + `"${itemTitle(item)}"\n\n`
      + `this skips the 30-day window and cannot be undone.`,
    )) return;
    setActionPending(item.id);
    setErrorMsg(null);
    try {
      const fn = httpsCallable(functions, 'hardDeleteFromTrash');
      await fn({ trashId: item.id });
      flashToast('permanently deleted');
    } catch (err) {
      console.warn('[TrashPage] hard delete failed', err);
      setErrorMsg(err?.message || 'delete failed');
    } finally {
      setActionPending(null);
    }
  };

  // Bulk-empty: hard-delete every item currently in trash. Implemented
  // client-side as a fan-out of hardDeleteFromTrash so the Cloud
  // Function side stays unchanged. Per-item failures are caught so
  // one bad row doesn't block the rest; the failure count surfaces in
  // the toast at the end.
  const onEmptyAll = async () => {
    if (items.length === 0) return;
    if (!window.confirm(
      `permanently delete all ${items.length} item${items.length === 1 ? '' : 's'} in trash?\n\n`
      + `this skips the 30-day window and cannot be undone.`,
    )) return;
    setActionPending('__bulk__');
    setErrorMsg(null);
    let failed = 0;
    const fn = httpsCallable(functions, 'hardDeleteFromTrash');
    for (const item of items) {
      try {
        await fn({ trashId: item.id });
      } catch (err) {
        console.warn('[TrashPage] empty-all per-item failed', item.id, err);
        failed += 1;
      }
    }
    setActionPending(null);
    if (failed === 0) flashToast('trash emptied');
    else flashToast(`emptied with ${failed} failure${failed === 1 ? '' : 's'}`);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      padding: '32px 24px',
      fontFamily: 'var(--mono)',
      color: 'var(--text)',
    }}>
      <div style={{ maxWidth: '720px', margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px',
        }}>
          <Wordmark />
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'transparent',
              border: '1px solid var(--border)',
              color: 'var(--text-dim)',
              padding: '6px 12px',
              borderRadius: 'var(--radius)',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(11px * var(--ui-scale, 1))',
              cursor: 'pointer',
            }}
            title="back"
          >
            ← back
          </button>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 8,
        }}>
          <h1 style={{
            fontFamily: 'var(--mono)',
            fontSize: 'calc(18px * var(--ui-scale, 1))',
            fontWeight: 600,
            margin: 0,
          }}>
            trash
          </h1>
          {/* Empty-trash button (Walter 2026-05-05): wipe everything
              that's currently in trash. Hidden when empty so the row
              doesn't tease an action with nothing to act on. */}
          {items.length > 0 && (
            <button
              onClick={onEmptyAll}
              disabled={actionPending === '__bulk__'}
              style={{
                background: 'transparent',
                border: '1px solid var(--danger, #d04a4a)',
                color: 'var(--danger, #d04a4a)',
                padding: '5px 10px',
                borderRadius: 'var(--radius)',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
                cursor: actionPending === '__bulk__' ? 'wait' : 'pointer',
                opacity: actionPending === '__bulk__' ? 0.6 : 1,
              }}
              title="permanently delete every item in trash"
            >
              {actionPending === '__bulk__' ? 'emptying…' : 'empty trash'}
            </button>
          )}
        </div>
        <p style={{
          fontSize: 'calc(12px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
          margin: '0 0 28px',
          lineHeight: 1.5,
        }}>
          deleted boards and lists land here for 30 days. restore brings them back to
          their original location (or a "Recovered" board if the parent was also deleted).
          after 30 days, items are permanently deleted.
        </p>

        {errorMsg && (
          <div style={{
            padding: '10px 12px',
            marginBottom: 12,
            background: 'var(--surface2)',
            border: '1px solid var(--danger, #d04a4a)',
            borderRadius: 'var(--radius)',
            color: 'var(--danger, #d04a4a)',
            fontSize: '12px',
          }}>
            {errorMsg}
          </div>
        )}

        {loading && (
          <div style={{ color: 'var(--text-dim)', fontSize: '12px' }}>loading…</div>
        )}

        {!loading && items.length === 0 && (
          <div style={{
            padding: '32px',
            textAlign: 'center',
            border: '1px dashed var(--border)',
            borderRadius: 'var(--radius)',
            color: 'var(--text-dimmer)',
            fontSize: '12px',
          }}>
            trash is empty.
          </div>
        )}

        {!loading && items.length > 0 && (
          <div style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            overflow: 'hidden',
          }}>
            {items.map(item => (
              <TrashRow
                key={item.id}
                item={item}
                pending={actionPending === item.id}
                onRestore={() => onRestore(item)}
                onHardDelete={() => onHardDelete(item)}
              />
            ))}
          </div>
        )}

        {toast && (
          <div style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--surface2)',
            border: '1px solid var(--accent-dim)',
            color: 'var(--text)',
            fontFamily: 'var(--mono)',
            fontSize: '12px',
            padding: '10px 16px',
            borderRadius: 'var(--radius)',
            boxShadow: 'var(--shadow-toast-light)',
            zIndex: 9999,
          }}>
            {toast}
          </div>
        )}
      </div>
    </div>
  );
}

function TrashRow({ item, pending, onRestore, onHardDelete }) {
  const title = itemTitle(item);
  const expiresMs = item.expiresAt?.toMillis?.() || 0;
  const daysLeft = Math.max(0, Math.ceil((expiresMs - Date.now()) / (24 * 60 * 60 * 1000)));
  const expiringSoon = daysLeft <= 3;
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 14px',
      borderTop: '1px solid var(--border)',
      background: 'var(--surface)',
    }}>
      <KindBadge kind={item.kind} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 'calc(13px * var(--ui-scale, 1))',
          color: 'var(--text)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {title || `(untitled ${item.kind})`}
        </div>
        <div style={{
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          color: 'var(--text-dimmer)',
          marginTop: '2px',
        }}>
          deleted {fmtRelative(item.deletedAt)}
          {item.kind === 'board' && Array.isArray(item.payload?.lists) && item.payload.lists.length > 0
            && <> · {item.payload.lists.length} list{item.payload.lists.length === 1 ? '' : 's'}</>}
          {' · '}
          <span style={{ color: expiringSoon ? 'var(--danger, #d04a4a)' : 'inherit' }}>
            {daysLeft === 0 ? 'expires today' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
          </span>
        </div>
      </div>
      <button
        onClick={onRestore}
        disabled={pending}
        style={{
          padding: '6px 12px',
          background: pending ? 'var(--accent-dim)' : 'var(--accent)',
          color: 'var(--bg)',
          border: '1px solid var(--accent)',
          borderRadius: 'var(--radius)',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(11px * var(--ui-scale, 1))',
          fontWeight: 600,
          cursor: pending ? 'wait' : 'pointer',
        }}
      >
        {pending ? '…' : 'restore'}
      </button>
      <button
        onClick={onHardDelete}
        disabled={pending}
        style={{
          padding: '6px 10px',
          background: 'transparent',
          color: 'var(--danger, #d04a4a)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          cursor: pending ? 'wait' : 'pointer',
          opacity: pending ? 0.5 : 1,
        }}
        title="permanently delete now (skip the 30-day window)"
      >
        delete now
      </button>
    </div>
  );
}

function KindBadge({ kind }) {
  return (
    <span style={{
      fontSize: '9px',
      color: 'var(--text-dimmer)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
      padding: '2px 6px',
      letterSpacing: '0.4px',
      textTransform: 'uppercase',
      flexShrink: 0,
    }}>
      {kind}
    </span>
  );
}

function itemTitle(item) {
  if (!item?.payload) return '';
  if (item.kind === 'list') return item.payload.list?.title || '';
  if (item.kind === 'board') return item.payload.board?.name || '';
  return '';
}

function fmtRelative(ts) {
  const ms = ts?.toMillis?.();
  if (!ms) return '';
  const diff = Date.now() - ms;
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  if (days < 1) {
    const hours = Math.floor(diff / (60 * 60 * 1000));
    if (hours < 1) {
      const mins = Math.floor(diff / (60 * 1000));
      return mins <= 1 ? 'just now' : `${mins} minutes ago`;
    }
    return `${hours}h ago`;
  }
  if (days < 30) return `${days}d ago`;
  return new Date(ms).toLocaleDateString();
}
